import { onUpdateCommissionCycleQuery } from "@/Common/api/commissionCycles/subscriptions";
import { ICommissionCycle } from "@/Common/api/types/CommissionCycle";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useEffect } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

type UseOnUpdateCommissionCycleParams = {
	locationId?: string;
	commissionCycleId?: string;
	returnFields?: string;
	onUpdateCallback?: (updated: ICommissionCycle | null) => void;
};

export const useOnUpdateCommissionCycle = ({
	locationId = "",
	commissionCycleId = "",
	returnFields,
	onUpdateCallback,
}: UseOnUpdateCommissionCycleParams) => {
	const queryClient = useQueryClient();
	const subscriptionKey = ["updateCommissionCycle", commissionCycleId];

	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();

	const subscription = getSubscription(subscriptionKey);

	subscription?.subscriptions?.onUpdateCommissionCycle?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateCommissionCycle", ICommissionCycle>) => {
			const response = result.data?.onUpdateCommissionCycle ?? null;
			if (onUpdateCallback) {
				onUpdateCallback(response);
			} else {
				defaultCallback(queryClient, response, locationId);
			}
		}
	);

	useEffect(() => {
		!!commissionCycleId &&
			addSubscription(
				subscriptionKey,
				[
					{
						context: "onUpdateCommissionCycle",
						query: onUpdateCommissionCycleQuery(returnFields),
						variables: { commissionCycleId },
					},
				],
				true
			);
		return () => deleteSubscription(subscriptionKey);
	}, [commissionCycleId]);
};

export const defaultCallback = (queryClient: QueryClient, updated: ICommissionCycle | null, locationId: string) => {
	queryClient?.setQueryData(["commissionCycles", locationId], (oldData: ICommissionCycle[]) => {
		return (
			oldData?.map((cycle) => {
				if (cycle._id === updated?._id) {
					return { ...cycle, ...updated };
				}
				return cycle;
			}) ?? []
		);
	});
};
